import React, { useEffect, useState, useRef } from 'react';

/**
 * AboutGalleryAdmin
 * ----------------------------------------------------------------------------
 * Kỹ-thuật-only panel inside the dashboard for managing the slideshow images
 * shown on the public About page. Supports the three named slots (one large
 * "main" frame and two smaller "side" frames) with per-slot upload, caption
 * editing, reordering (move up / down) and deletion.
 *
 * The panel calls the REST endpoints under `/api/about-gallery` defined in
 * server/index.js. Uses the same `token`-prop convention as the other
 * admin components (MailCampaignAdmin etc.) so it slots straight into the
 * existing Dashboard tabs.
 */
const SLOT_DEFS = [
  { key: 'main',       langKey: 'GALLERY_SLOT_MAIN',       fallback: 'Khung chính' },
  { key: 'sideTop',    langKey: 'GALLERY_SLOT_SIDE_TOP',    fallback: 'Khung phụ trái' },
  { key: 'sideBottom', langKey: 'GALLERY_SLOT_SIDE_BOTTOM', fallback: 'Khung phụ phải' }
];

const AboutGalleryAdmin = ({ token, lang = {} }) => {
  const [gallery, setGallery] = useState({ main: [], sideTop: [], sideBottom: [] });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [content, setContent] = useState({});
  const [contentDirty, setContentDirty] = useState(false);
  const [contentBusy, setContentBusy] = useState(false);
  const fileRefs = useRef({}); // per-slot file input refs

  const auth = { Authorization: `Bearer ${token}` };
  const show = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  const refresh = () => {
    fetch('/api/about-gallery')
      .then(r => r.json())
      .then(d => setGallery({
        main: Array.isArray(d.main) ? d.main : [],
        sideTop: Array.isArray(d.sideTop) ? d.sideTop : [],
        sideBottom: Array.isArray(d.sideBottom) ? d.sideBottom : []
      }))
      .catch(err => show('error', 'Không tải được gallery: ' + err.message));
    fetch('/api/about-content')
      .then(r => r.ok ? r.json() : {})
      .then(d => { setContent(d && typeof d === 'object' ? d : {}); setContentDirty(false); })
      .catch(() => {});
  };

  useEffect(() => { refresh(); }, []);

  const handleUpload = async (slot, e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('slot', slot);
      fd.append('image', file);
      const res = await fetch('/api/about-gallery', { method: 'POST', headers: auth, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload thất bại.');
      setGallery(data.gallery);
      show('success', 'Đã thêm ảnh.');
    } catch (err) {
      show('error', err.message);
    } finally {
      setBusy(false);
      if (fileRefs.current[slot]) fileRefs.current[slot].value = '';
    }
  };

  const handleCaption = async (slot, id, caption) => {
    try {
      const res = await fetch(`/api/about-gallery/${slot}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({ caption })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Cập nhật thất bại.');
      setGallery(data.gallery);
    } catch (err) {
      show('error', err.message);
    }
  };

  const handleMove = async (slot, id, direction) => {
    const list = gallery[slot];
    const idx = list.findIndex(it => it.id === id);
    if (idx === -1) return;
    const target = idx + direction;
    if (target < 0 || target >= list.length) return;
    try {
      const res = await fetch(`/api/about-gallery/${slot}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({ position: target })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Đổi thứ tự thất bại.');
      setGallery(data.gallery);
    } catch (err) {
      show('error', err.message);
    }
  };

  const handleDelete = async (slot, id) => {
    if (!window.confirm(lang.GALLERY_IMAGE_DELETE_CONFIRM || 'Xoá ảnh này khỏi gallery?')) return;
    try {
      const res = await fetch(`/api/about-gallery/${slot}/${id}`, {
        method: 'DELETE',
        headers: auth
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Xoá thất bại.');
      setGallery(data.gallery);
      show('success', 'Đã xoá ảnh.');
    } catch (err) {
      show('error', err.message);
    }
  };

  const setContentField = (k, v) => { setContent(prev => ({ ...prev, [k]: v })); setContentDirty(true); };
  const saveContent = async () => {
    setContentBusy(true);
    try {
      const res = await fetch('/api/about-content', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify(content)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Lưu thất bại.');
      setContent(data.content || {});
      setContentDirty(false);
      show('success', 'Đã lưu nội dung trang About.');
    } catch (err) {
      show('error', err.message);
    } finally { setContentBusy(false); }
  };

  const CONTENT_SECTIONS = [
    { title: 'Phần đầu', fields: [
      { k: 'label', label: 'Nhãn (section-label)', placeholder: 'About Us' },
      { k: 'title', label: 'Tiêu đề chính', placeholder: 'What is ISP Club?' },
      { k: 'description', label: 'Mô tả', placeholder: 'ISP Club is an academic club…', textarea: true }
    ] },
    { title: 'Số liệu (Stats)', fields: [
      { k: 'statMembers', label: 'Members — giá trị', placeholder: '150+' },
      { k: 'statMembersLabel', label: 'Members — nhãn', placeholder: 'Members' },
      { k: 'statCtfs', label: 'CTFs — giá trị', placeholder: '30+' },
      { k: 'statCtfsLabel', label: 'CTFs — nhãn', placeholder: 'CTFs Participated' },
      { k: 'statWorkshops', label: 'Workshops — giá trị', placeholder: '20+' },
      { k: 'statWorkshopsLabel', label: 'Workshops — nhãn', placeholder: 'Workshops Organized' },
      { k: 'statYears', label: 'Years — giá trị', placeholder: '7+' },
      { k: 'statYearsLabel', label: 'Years — nhãn', placeholder: 'Years Active' }
    ] },
    { title: 'Giá trị cốt lõi (Mission / Vision / Core Values)', fields: [
      { k: 'missionTitle', label: 'Mission — tiêu đề', placeholder: 'Mission' },
      { k: 'missionDesc', label: 'Mission — mô tả', placeholder: '…', textarea: true },
      { k: 'visionTitle', label: 'Vision — tiêu đề', placeholder: 'Vision' },
      { k: 'visionDesc', label: 'Vision — mô tả', placeholder: '…', textarea: true },
      { k: 'valuesTitle', label: 'Core Values — tiêu đề', placeholder: 'Core Values' },
      { k: 'valuesDesc', label: 'Core Values — mô tả', placeholder: '…', textarea: true }
    ] }
  ];

  return (
    <div className="admin-form-card" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <h3 style={{ margin: '0 0 6px' }}>Thông Tin CLB</h3>
      </div>

      {msg && (
        <div className={`alert alert-${msg.type === 'success' ? 'success' : 'danger'}`} style={{ marginBottom: 0 }}>
          {msg.text}
        </div>
      )}

      {SLOT_DEFS.map(({ key, langKey, fallback }) => {
        const list = gallery[key] || [];
        const label = lang[langKey] || fallback;
        return (
          <section key={key}>
            <h4 style={{ margin: '0 0 8px' }}>
              {label} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({list.length} ảnh)</span>
            </h4>
            <input
              ref={el => { if (el) fileRefs.current[key] = el; }}
              type="file"
              accept="image/*"
              onChange={(e) => handleUpload(key, e)}
              disabled={busy}
              style={{ marginBottom: 12 }}
            />

            {list.length === 0 ? (
              <p className="form-hint" style={{ margin: 0 }}>Chưa có ảnh nào. Upload để bắt đầu.</p>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 12
              }}>
                {list.map((im, idx) => (
                  <div key={im.id} style={{
                    display: 'flex', flexDirection: 'column', gap: 6,
                    padding: 8,
                    border: '1px solid var(--border-primary)',
                    borderRadius: 8,
                    background: 'var(--bg-secondary)'
                  }}>
                    <img
                      src={`/uploads/about-gallery/${im.filename}`}
                      alt={im.caption || ''}
                      style={{
                        width: '100%', aspectRatio: '4 / 3', objectFit: 'cover',
                        borderRadius: 6
                      }}
                    />
                    <input
                      type="text"
                      defaultValue={im.caption || ''}
                      placeholder={lang.GALLERY_CAPTION_PH || 'Caption (tuỳ chọn, hiển thị dưới ảnh)'}
                      onBlur={(e) => {
                        if (e.target.value !== (im.caption || '')) {
                          handleCaption(key, im.id, e.target.value);
                        }
                      }}
                      style={{ fontSize: 12, padding: '4px 6px' }}
                      maxLength={200}
                    />
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>#{idx + 1} / {list.length}</span>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          disabled={idx === 0}
                          onClick={() => handleMove(key, im.id, -1)}
                          title={lang.COMMON_MOVE_UP || 'Lên'}
                          style={{ padding: '2px 8px' }}
                        >▲</button>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          disabled={idx === list.length - 1}
                          onClick={() => handleMove(key, im.id, +1)}
                          title={lang.COMMON_MOVE_DOWN || 'Xuống'}
                          style={{ padding: '2px 8px' }}
                        >▼</button>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(key, im.id)}
                          title={lang.COMMON_DELETE || 'Xoá'}
                          style={{ padding: '2px 8px' }}
                        >✕</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}

      <section style={{ borderTop: '1px solid var(--border-primary)', paddingTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Chỉnh sửa nội dung trang About</h3>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={saveContent}
            disabled={contentBusy || !contentDirty}
          >
            {contentBusy ? 'Đang lưu...' : (contentDirty ? 'Lưu thay đổi' : 'Đã lưu')}
          </button>
        </div>
        {CONTENT_SECTIONS.map(sec => (
          <div key={sec.title} style={{ marginBottom: 18 }}>
            <h4 style={{ margin: '0 0 8px' }}>{sec.title}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
              {sec.fields.map(f => (
                <label key={f.k} style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{f.label}</span>
                  {f.textarea ? (
                    <textarea
                      rows={3}
                      value={content[f.k] || ''}
                      placeholder={f.placeholder}
                      onChange={e => setContentField(f.k, e.target.value)}
                      style={{ resize: 'vertical', minHeight: 60 }}
                    />
                  ) : (
                    <input
                      type="text"
                      value={content[f.k] || ''}
                      placeholder={f.placeholder}
                      onChange={e => setContentField(f.k, e.target.value)}
                    />
                  )}
                </label>
              ))}
            </div>
          </div>
        ))}
        <p className="form-hint" style={{ margin: 0 }}>Để trống một ô để dùng giá trị mặc định.</p>
      </section>
    </div>
  );
};

export default AboutGalleryAdmin;
